import React from 'react';

import {SINGLE_DATASET_INPUT_STEP_DATA_NAME} from 'analysis-tool/AnalysisStepDataNames';
import {PipelineStepDataKind, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import UserDefinedFunctionEditor from 'pipelines/user-defined-function/UserDefinedFunctionEditor';
import {InlineBannerType} from 'ui-library/banners/InlineBanner';

import SingleInputSourceSelectorEditor from '../editors/custom-editors/SingleInputSourceSelectorEditor';
import {StepTypeConfigUISchemaV2} from '../step-config-settings/StepConfigUISchemas';
import {
  BANNER_KEY,
  CUSTOM_COMPONENT_KEY,
  FIELD_ORDER_UI_SCHEMA_KEY,
  SHOW_DIVIDER_LINE_KEY,
  StepConfigEditorOverride,
} from '../StepConfigEditorTypes';

const PARAMETERIZED_SQL_QUERY_INFO_TEXT =
  'Write a SQL query against the data warehouse to create a table. Reference parameters with ' +
  '{{parameterName}} to fill values from the input dataset.';

export const WAREHOUSE_SQL_DATASET_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<
  PipelineStepType.WAREHOUSE_SQL_DATASET,
  0
> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['dataFrameName', 'parameterizedSQLQuery'],
  dataFrameName: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['dataFrameName'],
    dataFrameName: {
      displayName: 'Table name',
      configShapePath: ['dataFrameName'],
    },
  },
  parameterizedSQLQuery: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['parameterizedSQLQuery'],
    parameterizedSQLQuery: {
      displayName: 'SQL query',
      infoText: 'Write a SQL query against the data warehouse to create a table.',
      [CUSTOM_COMPONENT_KEY]: (props) => (
        <UserDefinedFunctionEditor {...props} fieldName="parameterizedSQLQuery" mode="sql" />
      ),
    },
  },
};

export const WAREHOUSE_SQL_DATASET_V1_UI_SCHEMA: StepTypeConfigUISchemaV2<
  PipelineStepType.WAREHOUSE_SQL_DATASET,
  1
> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['dataFrameName', 'parameterizedSQLQuery', 'inputSource', 'parameters'],
  dataFrameName: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['dataFrameName'],
    dataFrameName: {
      displayName: 'Table name',
      configShapePath: ['dataFrameName'],
    },
  },
  parameterizedSQLQuery: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['parameterizedSQLQuery'],
    parameterizedSQLQuery: {
      displayName: 'SQL query',
      infoText: PARAMETERIZED_SQL_QUERY_INFO_TEXT,
      [CUSTOM_COMPONENT_KEY]: (props) => (
        <UserDefinedFunctionEditor {...props} fieldName="parameterizedSQLQuery" mode="sql" />
      ),
    },
  },
  inputSource: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['inputSource'],
    inputSource: {
      [CUSTOM_COMPONENT_KEY]: (props) => (
        <SingleInputSourceSelectorEditor
          {...props}
          inputStepDataName={SINGLE_DATASET_INPUT_STEP_DATA_NAME}
          label="Parameters table"
          dataKind={PipelineStepDataKind.DATASET}
        />
      ),
    },
  },
  parameters: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['parameters'],
    parameters: {
      displayName: 'Parameters',
      configShapePath: ['parameters'],
      [BANNER_KEY]: {
        text:
          'Reference a parameter in your SQL query with {{parameterName}}. Each parameter takes its ' +
          'value from a column of the input dataset.',
        bannerType: InlineBannerType.INFO,
        placement: 'above',
      },
      elementUISchema: {
        [FIELD_ORDER_UI_SCHEMA_KEY]: ['systemName', 'columnSystemName', 'isMulti'],
        [SHOW_DIVIDER_LINE_KEY]: true,
        systemName: {
          displayName: 'Parameter name',
          configShapePath: ['systemName'],
          infoText: 'The name used to reference this parameter in the SQL query, e.g. {{parameterName}}.',
        },
        columnSystemName: {
          displayName: 'Input column',
          configShapePath: ['columnSystemName'],
        },
        isMulti: {
          displayName: 'Multiple values',
          configShapePath: ['isMulti'],
          editorOverride: StepConfigEditorOverride.CHECKBOX,
          fieldSettings: {
            checkboxLabel: 'Use all values (up to 10,000)',
          },
          infoText:
            'When enabled, the parameter resolves to all values in the column (up to 10,000). ' +
            'When disabled, it resolves to the value in the first row.',
        },
      },
    },
  },
};
